<template>
  <div class="fs-exclude">
    <s-row>
      <s-col span="3" class="back-button">
        <router-link to="/vat-recoverability">
          <s-icon class="align-self-center" name="arrow-left"></s-icon
          ><span>Back</span>
        </router-link>
      </s-col>
    </s-row>
    <s-row class="heading-decsription">
      <s-col span="8">
        <h3>Build a VAT recoverability matrix</h3>
        <p>
          Add tax codes to create your own VAT recoverability matrix for specific tax
          codes and countries.
        </p>
      </s-col>
    </s-row>
    <s-row>
      <s-col span="4">
        <label class="label-info"
          >Add up to {{ taxcodeLimit }} Avalara tax codes
          <div class="tooltip-wrap">
            <s-icon
              class="margin-left-xs"
              name="help-circle-filled"
              role="img"
              aria-label="account alert"
            ></s-icon>
            <div class="tooltip-content pad-all-sm">
              Add multiple tax codes by copying and pasting them in space
              separated format.
            </div>
          </div></label
        >
        <CustomSelectBox
          name="searchTaxcodes"
          placeholder="PF052132"
          :data="allTaxCodesData"
          :onSelect="onTaxCodeSelected"
          :onDeselect="onTaxCodeDeselected"
          :selectedItems="selectedTaxCodesParsed"
          :onInputChanged="searchTaxCodes"
          :key="selectBoxKey"
          :isInputDisabled="$store.state.loading"
          :isError="
            getSelectedTaxCodesOptions().length > taxcodeLimit ||
            isSearchInputError
          "
          :isSearcheable="true"
        />
        <div
          v-if="getSelectedTaxCodesOptions().length > taxcodeLimit"
          class="limit-error"
        >
          Add up to {{ taxcodeLimit }} tax codes.
        </div>
        <div v-else-if="isSearchInputError" class="limit-error">
          Invalid tax codes. Check that your tax codes are correct and separated
          by a space or comma.
        </div>
      </s-col>
      <s-col span="3">
        <div>
          <label for="select-country">Select countries</label>
          <CustomSelectBox
            name="searchCountries"
            placeholder="Select Countries"
            :data="allRegionsDataToBeUpdated"
            :onSelect="onRegionSelected"
            :onDeselect="onRegionDeselected"
            :onInputChanged="function noop() {}"
            :key="selectBoxKey"
            :isInputDisabled="$store.state.loading"
            :isError="false"
            :isSearcheable="true"
            inputid="select-country"
            id="select-country"
          >
          </CustomSelectBox>
        </div>
      </s-col>
      <s-col span="2">
        <div class="margin-top-lg search-row">
          <h3 class="margin-top-none">
            <a href="/vat-recoverability/search">
              <button class="ghost text-xs" title="clear all selections" link>
                Clear All
              </button>
            </a>
          </h3>
          <button
            @click="onVATRecoverabilitySearch"
            class="primary icon-leading text-xs margin-right-lg"
            :disabled="
              getSelectedRegionsToBeUpdated().length === 0 ||
              getSelectedTaxCodesOptions().length === 0 ||
              getSelectedTaxCodesOptions().length > taxcodeLimit
            "
          >
            <s-icon name="search"></s-icon>Create
          </button>
        </div>
      </s-col>
    </s-row>
    <s-row>
      <s-col span="12" class="margin-bottom-none pad-bottom-none">
        <hr class="margin-top-sm margin-bottom-sm" />
      </s-col>
    </s-row>
    <s-row
      v-if="
        getSelectedRegions().length === 0 ||
        selectedTaxCodesParsed.length === 0
      "
    >
      <s-col span="12">
        <div class="margin-top-xl placeholder">
          <img
            :src="`${$store.getters.assetsPath}/img/placeholder.png`"
            class="block margin-right-auto margin-left-auto"
          />
          <p class="text-center">
            Search for an Avalara tax code to get started.
          </p>
        </div>
      </s-col>
    </s-row>
    <VATRecoverabilityTable
      v-if="
        getSelectedRegions().length > 0 &&
        selectedTaxCodesParsed.length > 0
      "
      :taxcodes="selectedTaxCodesParsed"
      :countries="getSelectedRegions()"
      :codeToCountryRecoverability="codeToCountryRecoverability"
      :allRegionsData="allRegionsData"
    />
  </div>
</template>
<script lang="ts">
import { Component, Vue, Prop, Watch, Provide } from "vue-property-decorator";
import router from "@/router";
import axios from "axios";
import _ from "lodash";
import CustomSelectBox from "@/components/CustomSelectBox.vue";
import VATRecoverabilityTable from "@/components/VATRecoverabilityTable.vue";
import analyticsLayerUtil from "./../utils/index";

interface TaxCode {
  code: string;
  description: string;
  additionalDescription: string;
  categories: string[];
}

interface TaxCodeSearchResponse {
  code: string;
  short_desc: string;
}

interface VATRecoverabilityRule {
  country_code: string;
  country_name: string;
  avatax_code: string;
  recoverability_percentage: string;
  legislation_description: string;
  attributes: string;
  attributes_clean?: string;
  jurisdiction_name?: string;
}

interface VATRecoverabilityInfo {
  avatax_code: string;
  rules: VATRecoverabilityRule[];
}

interface SelectOption {
  label: string;
  value: string;
  selected?: boolean;
}

interface JurisdictionResponse {
  country_code: string;
  country_name: string;
  jurisdictions: object[];
}

@Component({
  components: {
    CustomSelectBox,
    VATRecoverabilityTable,
  },
})
export default class VATRecoverabilitySearch extends Vue {
  @Provide("$api") $api: any;
  @Prop({ default: "" }) private selectedTaxCodes!: string;
  @Prop({ default: "" }) private selectedCountries!: string;
  taxcodeLimit = 20;
  selectBoxKey = "0";
  selectedTaxCodesParsed: string[] =
    this.selectedTaxCodes.length > 0 ? this.selectedTaxCodes.split(",") : [];
  selectedCountryOptions: string[] =
    this.selectedCountries.length > 0 ? this.selectedCountries.split(",") : [];
  allRegionsData: SelectOption[] = [];
  allRegionsDataToBeUpdated: SelectOption[] = [];
  allTaxCodesData: SelectOption[] = [];
  vatRecoverabilities: VATRecoverabilityInfo[] = [];
  codeToCountryRecoverability: {
    [taxCode: string]: { [country: string]: VATRecoverabilityRule[] };
  } = {};
  isSearchInputError = false;

  async mounted() {
    await this.populateCountries();
    await this.loadVATRecoverabilityUsingRoute();
  }

  public async populateCountries() {
    try {
      const response = await this.$api.get(`/api/jurisdictions/`);

      const allJurisdictionsForCountries: JurisdictionResponse[] =
        response.data;
      allJurisdictionsForCountries.forEach((country) => {
        this.allRegionsData.push({
          label: country.country_name,
          value: country.country_code,
          selected: this.selectedCountryOptions.includes(country.country_code),
        });
      });
      this.allRegionsData.sort((a, b) => (a.label < b.label ? -1 : 1));
      this.allRegionsDataToBeUpdated = this.allRegionsData;
    } catch {
      this.$store.commit("stopLoading");
    }
  }

  public async loadVATRecoverabilityUsingRoute() {
    try {
      const response = await this.$api.post(
        "/api/taxcodes/",
        this.selectedTaxCodesParsed
      );

      const allTaxCodes: TaxCode[] = response.data;
      this.allTaxCodesData = allTaxCodes.map((value: TaxCode) => ({
        label: `${value.code} - ${value.description.replace(/\s{2,}/g, " ")}`,
        value: value.code,
        selected: true,
      }));
      if (
        this.selectedTaxCodesParsed.length > 0 &&
        this.selectedCountryOptions.length > 0
      ) {
        this.loadVATRecoverability();
      }
    } catch (error) {
      this.$store.commit("stopLoading");
    }
  }

  public loadVATRecoverability(): void {
    if (this.getSelectedTaxCodesOptions().length > this.taxcodeLimit) {
      return;
    }
    this.$store.commit("startLoading");
    this.$api
      .post("/api/vat-recoverability/", {
        taxcodes: this.selectedTaxCodesParsed,
        country_codes: this.getSelectedRegions(),
      })
      .then((response) => {
        this.vatRecoverabilities = response.data;
        this.updateCodeToCountryRecoverabilityMap();
        this.captureVATRecoverabilitySearch(
          this.selectedTaxCodesParsed,
          this.getSelectedRegions()
        );
        this.$store.commit("stopLoading");
      })
      .catch((error) => this.$store.commit("stopLoading"));
  }

  public captureVATRecoverabilitySearch(
    taxcodes: string[],
    countries: string[]
  ) {
    const props = {
      vatRecoverabilityMatrix: {
        vatRecoverabilityMatrixInfo: {
          taxCodes: taxcodes.join(","),
          selectedCountries: countries,
        },
      },
    };
    analyticsLayerUtil.addToDataLayer(props);
    if (this.$store.getters.analyticsEnabled) {
      window._satellite.track("vat_recoverability_build");
    }
  }

  public updateCodeToCountryRecoverabilityMap(): void {
    this.codeToCountryRecoverability = {};
    this.selectedTaxCodesParsed.forEach((taxCode: string) => {
      Vue.set(this.codeToCountryRecoverability, taxCode, {});
    });
    const normalizeCountryName = (countryCode: string, apiCountryName: string): string => {
      if (this.allRegionsData && this.allRegionsData.length > 0) {
        const region = this.allRegionsData.find(r => r.value === countryCode);
        if (region) {
          return region.label;
        }
      }
      return apiCountryName;
    };
    this.vatRecoverabilities.forEach((vatRecoverability: VATRecoverabilityInfo) => {
      const taxCode: string = vatRecoverability.avatax_code;
      if (!this.codeToCountryRecoverability[taxCode]) {
        Vue.set(this.codeToCountryRecoverability, taxCode, {});
      }
      vatRecoverability.rules.forEach((rule: VATRecoverabilityRule) => {
        if (rule.avatax_code === taxCode) {
          const normalizedCountryName = normalizeCountryName(rule.country_code, rule.country_name);
          if (!this.codeToCountryRecoverability[taxCode][normalizedCountryName]) {
            Vue.set(this.codeToCountryRecoverability[taxCode], normalizedCountryName, []);
          }
          const normalizedRule = {
            ...rule,
            country_name: normalizedCountryName
          };
          this.codeToCountryRecoverability[taxCode][normalizedCountryName].push(normalizedRule);
        }
      });
    });
  }

  public bulkSelectTaxcodes(searchQuery: string) {
    const taxcodes =
      searchQuery.indexOf(" ") > 0
        ? searchQuery.trim().split(/\s+/)
        : searchQuery.trim().split(",");

    const uniqTaxcodes = _.uniq(taxcodes);
    this.$store.commit("startLoading");
    this.$api
      .post("/api/taxcodes/", uniqTaxcodes)
      .then((response) => {
        const bulkTaxCodes: TaxCode[] = _.uniqBy(
          response.data,
          (taxcode) => taxcode.code
        );
        const isInputValid = uniqTaxcodes.length === bulkTaxCodes.length;
        if (!isInputValid) {
          this.isSearchInputError = true;
          this.$store.commit("stopLoading");
          return;
        }
        this.isSearchInputError = false;
        const bulkTaxCodesData: SelectOption[] = bulkTaxCodes.map(
          (value: TaxCode) => ({
            label: `${value.code} - ${value.description.replace(
              /\s{2,}/g,
              " "
            )}`,
            value: value.code,
            selected: true,
          })
        );
        const allTaxCodesDataDeduped = this.allTaxCodesData.filter(
          (data) =>
            bulkTaxCodes
              .map((bulkTaxCode) => bulkTaxCode.code)
              .indexOf(data.value) < 0
        );
        this.allTaxCodesData = [...allTaxCodesDataDeduped, ...bulkTaxCodesData];
        this.selectBoxKey = this.selectBoxKey + 1;
        this.$store.commit("stopLoading");
      })
      .catch((error) => this.$store.commit("stopLoading"));
  }

  public searchTaxCodes(e: any): void {
    this.isSearchInputError = false;
    const searchQuery: string = e.detail.inputValue.trim();
    const pattern = /^([a-zA-Z0-9 ,]*)$/;
    if (searchQuery === "" || !pattern.test(searchQuery)) return;
    if (searchQuery.indexOf(",") >= 0 || searchQuery.indexOf(" ") >= 0) {
      this.bulkSelectTaxcodes(searchQuery);
    } else {
      this.$api
        .get(`/api/taxcode_autosuggest/?searchterm=${searchQuery}`)
        .then((response) => {
          const selectedTaxCodesData: SelectOption[] =
            this.allTaxCodesData.filter((taxCodeData) => {
              return taxCodeData.selected;
            });
          const searchedTaxCodes: TaxCodeSearchResponse[] = response.data
            .results
            ? response.data.results
            : [];

          const searchedTaxCodesFiltered: TaxCodeSearchResponse[] =
            searchedTaxCodes.filter(
              (taxCode) =>
                selectedTaxCodesData
                  .map((taxCodeData) => taxCodeData.value)
                  .indexOf(taxCode.code) < 0
            );
          const searchedTaxCodesData: SelectOption[] =
            searchedTaxCodesFiltered.map((searchResponse) => ({
              label: `${
                searchResponse.code
              } - ${searchResponse.short_desc.replace(/\s{2,}/g, " ")}`,
              value: searchResponse.code,
              selected: undefined,
            }));
          this.allTaxCodesData = [
            ...selectedTaxCodesData,
            ...searchedTaxCodesData,
          ];
        })
        .catch((error) => this.$store.commit("stopLoading"));
    }
  }

  public onRegionDeselected(e: any) {
    const selectedOption = e.detail.item.value;
    this.allRegionsDataToBeUpdated = this.allRegionsDataToBeUpdated.map(
      (regionData) =>
        regionData.value === selectedOption
          ? { ...regionData, selected: undefined }
          : regionData
    );
  }

  public onRegionSelected(e: any) {
    const selectedOption = e.detail.item.value;
    this.allRegionsDataToBeUpdated = this.allRegionsDataToBeUpdated.map(
      (regionData) =>
        regionData.value === selectedOption
          ? { ...regionData, selected: true }
          : regionData
    );
  }

  public onTaxCodeSelected(e: any): void {
    const selectedOption = e.detail.item.value;
    this.allTaxCodesData = this.allTaxCodesData.map((taxCodeData) =>
      taxCodeData.value === selectedOption
        ? { ...taxCodeData, selected: true }
        : taxCodeData
    );
  }

  public onTaxCodeDeselected(e: any): void {
    this.allTaxCodesData = this.allTaxCodesData.map((taxCodeData) => {
      return taxCodeData.value === e.detail.item.value
        ? { ...taxCodeData, selected: undefined }
        : taxCodeData;
    });
    this.selectBoxKey = this.selectBoxKey + 1;
  }

  public onVATRecoverabilitySearch() {
    this.selectedTaxCodesParsed = this.getSelectedTaxCodesOptions();
    this.allRegionsData = this.allRegionsDataToBeUpdated;
    this.updateRoute();
    this.loadVATRecoverability();
  }

  public updateRoute(): void {
    const taxcodes: string = this.selectedTaxCodesParsed.sort().join(",");
    const countries = this.getSelectedRegions().sort().join(",");
    router.push({
      name: "vat_recoverability_search",
      query: {
        taxcodes,
        countries,
      },
    });
  }

  public getSelectedRegions(): string[] {
    return this.allRegionsData
      .filter((region) => region.selected)
      .map((region) => region.value);
  }

  public getSelectedRegionsToBeUpdated(): string[] {
    return this.allRegionsDataToBeUpdated
      .filter((region) => region.selected)
      .map((region) => region.value);
  }

  public getSelectedTaxCodesOptions() {
    const filteredTaxCodes = this.allTaxCodesData
      .filter((taxCodesData) => taxCodesData.selected)
      .map((taxCodesData) => taxCodesData.value);
    return filteredTaxCodes;
  }
}
</script>
<style scoped lang="scss">
.heading-decsription {
  * {
    line-height: 1.4rem;
  }
  p {
    font-size: 0.88rem;
    font-weight: 400;
    color: var(--color-gray-dark);
  }
  h3 {
    font-weight: bold;
    font-size: 1.7rem;
    color: var(--color-black);
    text-align: left;
  }
}

.search-row {
  h3 {
    float: right;
  }
  button {
    float: right;
  }
  button.ghost {
    color: var(--color-blue);
  }
}

s-col {
  padding-bottom: 0.6rem;
}
hr {
  border-width: 0.15rem 0 0;
}
.limit-error {
  color: var(--color-red-dark);
  font-weight: 600;
}
.label-info {
  s-icon {
    color: var(--color-blue-dark);
  }
}
.tooltip-wrap {
  display: initial;
  line-height: normal;
}
.tooltip-wrap .tooltip-content {
  display: none;
  position: fixed;
  background-color: var(--color-gray-lightest);
  z-index: 1;
  max-width: 300px;
}
.tooltip-wrap:hover .tooltip-content {
  display: initial;
}
.back-button {
  a:visited {
    color: var(--color-blue-dark);
  }
  span {
    line-height: 2;
  }
}
</style>
